import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { SchoolLevelChartComponent } from '../charts/school-level-chart.component';
import { ReportChromeService } from '../report-chrome.service';
import { ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { GradeLevelRef, SubjectReportView } from '../report.model';

/**
 * Section « niveau scolaire des compétences » d'une matière.
 *
 * L'axe des ordonnées n'est pas déduit des points : il arrive en entrée
 * (`scale`), parce que c'est le référentiel du back qui fixe l'ordre des
 * niveaux. Une matière dont aucune compétence n'atteint la terminale doit
 * quand même montrer la terminale en haut de l'axe, sinon deux panneaux
 * voisins n'auraient pas la même échelle et se compareraient à tort.
 *
 * L'hôte est en `display: contents` : la feuille de styles cible
 * `.report-main > .section` et un élément de composant intercalé deviendrait
 * l'enfant de la grille à sa place.
 */
@Component({
  selector: 's2e-report-school-levels',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportTPipe, ReportRevealDirective, SchoolLevelChartComponent],
  templateUrl: './subject-school-levels.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectSchoolLevelsSection {
  private readonly i18n = inject(ReportI18nService);
  protected readonly chrome = inject(ReportChromeService);

  readonly subject = input.required<SubjectReportView>();
  /** Référentiel ordonné des niveaux : l'axe des ordonnées, à lui seul. */
  readonly scale = input.required<GradeLevelRef[]>();

  /**
   * Sens de lecture d'un code isolé.
   *
   * Distinct de celui de la page : `م1` mêle une lettre arabe et un chiffre, et
   * l'algorithme bidi le rendrait « 1م » si on le laissait faire.
   */
  protected readonly codeDir = computed(() => this.i18n.codeDirection());

  /* Les graphiques ne traduisent pas : la section leur passe des chaînes déjà
     résolues, ce qui évite d'injecter le moteur i18n dans six composants. */
  protected readonly ariaLabel = computed(() =>
    this.i18n.t('schoolLevel.aria', { count: this.subject().charts.schoolLevel.length }),
  );
  protected readonly levelHeading = computed(() => this.i18n.t('schoolLevel.level'));
}
